import { useEffect, useState } from "react";
import { jobService } from "../../../../services/job-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge, formatDate } from "./admin-ui";

function AdminBlogsPage() {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState("posts"); // "posts" or "categories"
    const [message, setMessage] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    // Filters for blogs
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    // Modal state for Post CRUD
    const [isPostModalOpen, setIsPostModalOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null); // null if creating
    const [postForm, setPostForm] = useState({
        title: "",
        description: "",
        category_id: "",
        author_name: "",
        status: "draft",
        is_featured: false,
        seo_title: "",
        seo_description: "",
        tags: ""
    });
    const [featuredImageFile, setFeaturedImageFile] = useState(null);

    // Form state for Categories
    const [newCategoryName, setNewCategoryName] = useState("");

    const loadData = async () => {
        try {
            const catData = await jobService.blogCategories();
            setCategories(catData.categories || []);

            const blogParams = {};
            if (searchTerm) blogParams.search = searchTerm;
            if (filterCategory) blogParams.categoryId = filterCategory;
            if (filterStatus) blogParams.status = filterStatus;
            
            const blogData = await jobService.blogs(blogParams);
            setBlogs(blogData.blogs || []);
        } catch (err) {
            setErrorMsg(err.message || "Failed to load data.");
        }
    };

    useEffect(() => {
        loadData();
    }, [searchTerm, filterCategory, filterStatus]);

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) return;
        try {
            await jobService.adminCreateBlogCategory({ name: newCategoryName });
            setMessage("Category created successfully!");
            setNewCategoryName("");
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to create category.");
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        try {
            await jobService.adminDeleteBlogCategory(id);
            setMessage("Category deleted successfully.");
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to delete category.");
        }
    };

    const handleOpenPostModal = (post = null) => {
        if (post) {
            setEditingPost(post);
            setPostForm({
                title: post.title || "",
                description: post.description || "",
                category_id: post.category_id || "",
                author_name: post.author_name || "",
                status: post.status || "draft",
                is_featured: !!post.is_featured,
                seo_title: post.seo_title || "",
                seo_description: post.seo_description || "",
                tags: post.tags || ""
            });
        } else {
            setEditingPost(null);
            setPostForm({
                title: "",
                description: "",
                category_id: categories[0]?.id || "",
                author_name: "Admin",
                status: "draft",
                is_featured: false,
                seo_title: "",
                seo_description: "",
                tags: ""
            });
        }
        setFeaturedImageFile(null);
        setIsPostModalOpen(true);
    };

    const handleSavePost = async (e) => {
        e.preventDefault();
        try {
            let postId;
            if (editingPost) {
                postId = editingPost.id;
                await jobService.adminUpdateBlogPost(postId, postForm);
            } else {
                const newPost = await jobService.adminCreateBlogPost(postForm);
                postId = newPost.id || newPost.blog?.id;
            }

            if (featuredImageFile && postId) {
                await jobService.adminUploadBlogImage(postId, featuredImageFile);
            }

            setMessage("Blog post saved successfully!");
            setIsPostModalOpen(false);
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to save blog post.");
        }
    };

    const handleDeletePost = async (id) => {
        if (!window.confirm("Are you sure you want to delete this blog post?")) return;
        try {
            await jobService.adminDeleteBlogPost(id);
            setMessage("Blog post deleted successfully.");
            loadData();
        } catch (err) {
            setErrorMsg(err.message || "Failed to delete post.");
        }
    };

    return (
        <>
            <AdminPageHeader title="Blog Management" subtitle="Manage articles, categories, and tags" />
            
            {message && <div className="alert alert-success alert-dismissible fade show" role="alert">
                {message}
                <button type="button" className="btn-close" onClick={() => setMessage("")}></button>
            </div>}
            
            {errorMsg && <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {errorMsg}
                <button type="button" className="btn-close" onClick={() => setErrorMsg("")}></button>
            </div>}

            <div className="tabbable-line m-b30">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button className={`nav-link ${activeTab === "posts" ? "active font-weight-bold text-primary" : ""}`} onClick={() => setActiveTab("posts")}>
                            Blog Posts ({blogs.length})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${activeTab === "categories" ? "active font-weight-bold text-primary" : ""}`} onClick={() => setActiveTab("categories")}>
                            Categories ({categories.length})
                        </button>
                    </li>
                </ul>
            </div>

            {activeTab === "posts" ? (
                <AdminPanel 
                    title="All Blogs" 
                    icon="fa fa-newspaper"
                    right={
                        <button className="btn btn-primary btn-sm" onClick={() => handleOpenPostModal()}>
                            <i className="fa fa-plus me-1" /> Add New Post
                        </button>
                    }
                >
                    {/* Filters Toolbar */}
                    <div className="row g-3 m-b20 align-items-center">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0"><i className="fa fa-search text-muted" /></span>
                                <input 
                                    type="text" 
                                    className="form-control border-start-0" 
                                    placeholder="Search by title, tags..." 
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)} 
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                                <option value="">All Categories</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="published">Published</option>
                                <option value="draft">Draft</option>
                            </select>
                        </div>
                        <div className="col-md-2 text-end">
                            <button className="btn btn-outline-secondary w-100" onClick={() => { setSearchTerm(""); setFilterCategory(""); setFilterStatus(""); }}>
                                Reset
                            </button>
                        </div>
                    </div>

                    {blogs.length === 0 ? (
                        <EmptyState text="No blog posts found matching criteria." />
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover table-striped align-middle">
                                <thead>
                                    <tr>
                                        <th>Featured Image</th>
                                        <th>Title</th>
                                        <th>Category</th>
                                        <th>Author</th>
                                        <th>Views</th>
                                        <th>Status</th>
                                        <th>Featured</th>
                                        <th>Created At</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blogs.map(blog => (
                                        <tr key={blog.id}>
                                            <td>
                                                {blog.featured_image_url ? (
                                                    <img 
                                                        src={blog.featured_image_url} 
                                                        alt={blog.title} 
                                                        style={{ width: "60px", height: "40px", objectFit: "cover", borderRadius: "4px" }} 
                                                    />
                                                ) : (
                                                    <div className="bg-light border d-flex align-items-center justify-content-center" style={{ width: "60px", height: "40px", borderRadius: "4px" }}>
                                                        <i className="fa fa-image text-muted" />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="fw-bold">{blog.title}</div>
                                                <small className="text-muted">{blog.read_time ? `${blog.read_time} min read` : "1 min read"}</small>
                                            </td>
                                            <td>{blog.category_name || <span className="text-muted">Uncategorized</span>}</td>
                                            <td>{blog.author_name}</td>
                                            <td><span className="badge bg-light text-dark"><i className="fa fa-eye me-1 text-primary" />{blog.views || 0}</span></td>
                                            <td><StatusBadge status={blog.status} /></td>
                                            <td>
                                                {blog.is_featured ? (
                                                    <span className="badge bg-warning text-dark"><i className="fa fa-star me-1" />Yes</span>
                                                ) : (
                                                    <span className="text-muted">No</span>
                                                )}
                                            </td>
                                            <td>{formatDate(blog.created_at)}</td>
                                            <td className="text-end">
                                                <div className="btn-group">
                                                    <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenPostModal(blog)}>
                                                        <i className="fa fa-edit" />
                                                    </button>
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeletePost(blog.id)}>
                                                        <i className="fa fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </AdminPanel>
            ) : (
                <div className="row">
                    <div className="col-md-4">
                        <AdminPanel title="Add New Category" icon="fa fa-folder-plus">
                            <form onSubmit={handleCreateCategory}>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Category Name</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="e.g., Career Advice" 
                                        value={newCategoryName} 
                                        onChange={(e) => setNewCategoryName(e.target.value)} 
                                        required 
                                    />
                                </div>
                                <button type="submit" className="btn btn-primary w-100">Add Category</button>
                            </form>
                        </AdminPanel>
                    </div>
                    <div className="col-md-8">
                        <AdminPanel title="Category List" icon="fa fa-list">
                            {categories.length === 0 ? (
                                <EmptyState text="No categories available." />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Slug</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {categories.map(cat => (
                                                <tr key={cat.id}>
                                                    <td className="fw-semibold">{cat.name}</td>
                                                    <td><code>{cat.slug}</code></td>
                                                    <td className="text-end">
                                                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteCategory(cat.id)}>
                                                            <i className="fa fa-trash" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </AdminPanel>
                    </div>
                </div>
            )}

            {/* Modal for Post creation/editing */}
            {isPostModalOpen && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <form onSubmit={handleSavePost}>
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        {editingPost ? "Edit Blog Post" : "Create Blog Post"}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setIsPostModalOpen(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-8">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Title</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={postForm.title} 
                                                    onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} 
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Category</label>
                                                <select 
                                                    className="form-select" 
                                                    value={postForm.category_id} 
                                                    onChange={(e) => setPostForm({ ...postForm, category_id: e.target.value })}
                                                    required
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Author Name</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={postForm.author_name} 
                                                    onChange={(e) => setPostForm({ ...postForm, author_name: e.target.value })} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Status</label>
                                                <select 
                                                    className="form-select" 
                                                    value={postForm.status} 
                                                    onChange={(e) => setPostForm({ ...postForm, status: e.target.value })}
                                                >
                                                    <option value="draft">Draft</option>
                                                    <option value="published">Published</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex align-items-center">
                                            <div className="form-check mt-3">
                                                <input 
                                                    type="checkbox" 
                                                    className="form-check-input" 
                                                    id="isFeatured" 
                                                    checked={postForm.is_featured} 
                                                    onChange={(e) => setPostForm({ ...postForm, is_featured: e.target.checked })} 
                                                />
                                                <label className="form-check-label fw-semibold" htmlFor="isFeatured">Featured Post</label>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Content (HTML allowed)</label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows="8" 
                                                    value={postForm.description} 
                                                    onChange={(e) => setPostForm({ ...postForm, description: e.target.value })} 
                                                    required 
                                                    placeholder="HTML content for blog body..."
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Featured Image</label>
                                                <input 
                                                    type="file" 
                                                    className="form-control" 
                                                    accept="image/*" 
                                                    onChange={(e) => setFeaturedImageFile(e.target.files[0])} 
                                                />
                                                {editingPost?.featured_image_url && !featuredImageFile && (
                                                    <div className="mt-2">
                                                        <small className="text-muted d-block">Current image:</small>
                                                        <img src={editingPost.featured_image_url} alt="Current Featured" style={{ maxHeight: "80px", borderRadius: "4px" }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">Tags (comma-separated)</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="career, advice, resume" 
                                                    value={postForm.tags} 
                                                    onChange={(e) => setPostForm({ ...postForm, tags: e.target.value })} 
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">SEO Title</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={postForm.seo_title} 
                                                    onChange={(e) => setPostForm({ ...postForm, seo_title: e.target.value })} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">SEO Description</label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows="2" 
                                                    value={postForm.seo_description} 
                                                    onChange={(e) => setPostForm({ ...postForm, seo_description: e.target.value })} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsPostModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">Save Changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default AdminBlogsPage;
